import { Clickable } from '@shared/Frontend/components/clickable'
import { SVGIcon } from '@shared/Frontend/components/svg'
import { useInView } from '@shared/Frontend/hooks/useInView'
import { pushDatalayer } from '@shared/Frontend/seo/datalayer'
import { cn } from '@shared/Frontend/utils/addToClassName'
import { slugify } from '@shared/Frontend/utils/slugify'
import * as React from 'react'
import { InnerHtmlWrapper } from '../../../../../../../EntryPoint/Frontend/legacy/public_site/components/utils/InnerHtmlWrapper'
import { ExpandableBlock } from '../../blocks.state'
import { NestedBlocks } from '../nestedBlock/nestedBlocks.layout'

export const ExpandableBlockLayout = (
  props: ExpandableBlock & {
    id: string
    videoCookieAccepted: boolean
    acceptVideoCookie: () => void
  }
) => {
  const { id, label, title, intro, items, isPrintMode, primaryUpdater, secondaryUpdater, showUpdaterAsLink } = props
  const { inView, ref } = useInView({ threshold: 0.1 })
  const hasPrimaryUpdater = primaryUpdater && primaryUpdater.title && primaryUpdater.url
  const hasSecondaryUpdater = secondaryUpdater && secondaryUpdater.title && secondaryUpdater.url
  return (
    <section
      id={id}
      ref={ref}
      className={cn('block block--list block--expand ls-block-spacing', inView && 'inview')}
    >
      <div className="block__inner ls-container">
        <div className="block__layout ls-columns">
          {(label || title || intro) && (
            <header className="block__header ls-columns__header">
              <div className="block__header-inner ls-reversed-column">
                {title && (
                  <h2 id={slugify(title)} className="block__title title--l">
                    {title}
                  </h2>
                )}
                {label && <p className="block__label">{label}</p>}
              </div>
              {intro && <div className="block__intro" dangerouslySetInnerHTML={{ __html: intro }} />}
            </header>
          )}

          <div className="block__content ls-columns__main">
            {items &&
              items.length > 0 &&
              items.map((item, index) => (
                <ExpandableLayout
                  key={index}
                  id={`expandable-${slugify(item.title)}`}
                  item={item}
                  isPrintMode={isPrintMode}
                >
                  <NestedBlocks nestedBlocks={item.nestedBlocks} {...props} />
                </ExpandableLayout>
              ))}
          </div>

          {/* NB: Van Oord currently uses only 1 block link in the expand block  */}
          {(hasPrimaryUpdater || hasSecondaryUpdater) && (
            <footer className="block__footer ls-columns__sidebar">
              <div className="ls-clickables">
                {hasPrimaryUpdater && (
                  <div className="block__action ls-clickable">
                    <Clickable
                      elementKind="link"
                      styleKind={showUpdaterAsLink ? 'link' : 'button'}
                      {...primaryUpdater}
                      className={
                        showUpdaterAsLink
                          ? 'block__clickable block__clickable--secondary link'
                          : 'block__clickable block__clickable--primary link'
                      }
                      onClick={() =>
                        pushDatalayer({
                          category: 'ExpandableBlock',
                          action: primaryUpdater.title
                        })
                      }
                    />
                  </div>
                )}
                {/* {hasSecondaryUpdater && (
                    <div className="block__action ls-clickable">
                      <Clickable
                        elementKind="link"
                        styleKind="link"
                        {...secondaryUpdater}
                        className="block__clickable block__clickable--secondary link"
                        onClick={() =>
                          pushDatalayer({
                            category: 'ExpandableBlock',
                            action: secondaryUpdater.title
                          })
                        }
                      />
                    </div>
                  )} */}
              </div>
            </footer>
          )}
        </div>
      </div>
    </section>
  )
}

export const ExpandableLayout = (props: {
  id: string
  item: ExpandableBlock['items'][number]
  children: React.ReactNode
  isPrintMode: boolean
}): JSX.Element => {
  const { id, item, isPrintMode, children } = props
  const [open, setOpen] = React.useState<boolean>(false)

  return (
    <details
      className={cn('block__item blocklist__link expand', open ? 'expander--open' : 'expander--closed')}
      open={open || isPrintMode}
      {...{
        onToggle: ({ currentTarget }) => {
          if (!isPrintMode) {
            setOpen(currentTarget.open)
            if (currentTarget.open) {
              pushDatalayer({
                category: 'Expandable',
                label: item.title,
                action: 'open_item'
              })
            }
          }
        }
      }}
    >
      <summary id={id} className="expand__summary">
        <p className="blocklist__title expand__title title--s">{item.title}</p>
        {item.description && <p className="blocklist__subtitle expand__description">{item.description}</p>}
        <SVGIcon id={open ? 'chevron-up' : 'chevron-down'} />
      </summary>

      <div className="block__text blocklist__content expand__body">
        {item.body && <InnerHtmlWrapper className="expand__content" text={item.body} />}
        {children && <div className="expand__blocks">{children}</div>}
      </div>
    </details>
  )
}
